import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Check } from 'lucide-react';
import { getAuthHeaders } from '../lib/auth';
import jonathanAbout from '../assets/jonathan-about.jpg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AboutPage() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, { headers: { ...getAuthHeaders() } });
        if (response.ok) setUser(await response.json());
      } catch {}
    };
    checkAuth();
  }, []);

  const whatIveDone = [
    "Planted and led a local church from the ground up, building out pastoral teams, governance structures, and ministry departments from scratch",
    "Pastored a congregation for seven years, growing its ministries and its people",
    "Served nationally in youth ministry leadership, training and equipping young leaders across a network of churches",
    "Led large-scale mobilization efforts, including coordinating city and county-wide prayer and outreach initiatives that brought together hundreds of churches",
    "Built out organizational and governance frameworks for a growing church, including formal delegation structures and department leadership",
    "Authored long-form written works calling the church back to revival conviction and calling the next generation to purpose",
    "Built a portfolio of technology ventures under my company, Bethany Labs — of which SermonSift is the flagship product",
  ];

  return (
    <div className="min-h-screen bg-[#fafaf9]" data-testid="about-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-stone-600 hover:text-[#1e3a8a]">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              <span className="hidden sm:inline">Back</span>
            </Link>
            <Link to="/" className="font-serif text-xl font-bold text-[#1e3a8a]">SermonSift</Link>
            <div className="hidden sm:flex items-center gap-6">
              <Link to="/pricing" className="text-sm text-stone-500 hover:text-[#1e3a8a] transition-colors">Pricing</Link>
              <Link to="/blog" className="text-sm text-stone-500 hover:text-[#1e3a8a] transition-colors">Blog</Link>
            </div>
            <div className="w-24 text-right">
              {user ? (
                <Link to="/dashboard">
                  <Button variant="ghost" className="text-[#1e3a8a]">Dashboard</Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button variant="ghost" className="text-[#1e3a8a]">Sign In</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">

          {/* Hero: photo + Who I Am */}
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-16 items-start mb-20 animate-fade-in">
            <div className="flex justify-center lg:justify-start">
              <div className="relative w-full max-w-sm">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#1e3a8a]/10 to-[#0ea5e9]/10 rounded-[2rem] -z-10"></div>
                <img
                  src={jonathanAbout}
                  alt="Pastor Jonathan Ogle, founder of SermonSift"
                  className="w-full rounded-3xl border border-stone-200 shadow-xl object-cover aspect-[4/5]"
                />
                <div className="mt-4 text-center lg:text-left">
                  <p className="font-serif font-bold text-[#1e3a8a]">Pastor Jonathan Ogle</p>
                  <p className="text-sm text-stone-500">Founder, SermonSift &amp; Bethany Labs</p>
                </div>
              </div>
            </div>

            <div>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a8a] mb-2 leading-tight">
                About the Founder
              </h1>
              <p className="text-lg text-stone-500 mb-6">Who I Am</p>
              <p className="text-lg text-stone-600 mb-4 leading-relaxed">
                I'm Jonathan Ogle — a pastor, builder, and lifelong student of Scripture who believes technology, rightly stewarded, can multiply ministry rather than dilute it.
              </p>
              <p className="text-stone-600 mb-4 leading-relaxed">
                I currently serve as the Founder and Lead Pastor of The Anchor Church in North Antrim, Northern Ireland, a congregation I planted in October 2023 out of a conviction that the local church still has a vital, unfinished role to play in this generation. Before that, I spent seven years pastoring a growing congregation, and prior to that I served nationally in youth leadership, working across Ireland to disciple and equip the next generation of church leaders. Those years in the trenches of local ministry — preaching weekly, walking with people through crisis and celebration, building teams, and shepherding growth — are the soil SermonSift grew out of.
              </p>
              <p className="text-stone-600 leading-relaxed">
                My path here wasn't purely theological, either. Before stepping into full-time ministry leadership, I worked in management consulting and in the legal sector, and spent time in the property industry. That background in strategy, research, and operational systems has shaped the way I think about ministry: not in opposition to excellence and structure, but as a partner to it. I hold to a simple framework — prayer before strategy, excellence without performance, Scripture as the final authority, and a long-term view over quick wins. Those convictions shape everything I build, including this product.
              </p>
            </div>
          </div>

          {/* What I've Done */}
          <div className="mb-20">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1e3a8a] mb-8">
              What I've Done
            </h2>
            <p className="text-stone-600 mb-6">Over the past two decades I've:</p>
            <ul className="space-y-4">
              {whatIveDone.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-stone-700">
                  <Check className="w-5 h-5 text-[#0ea5e9] flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* What I Do Now */}
          <div className="mb-20 max-w-3xl">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1e3a8a] mb-6">
              What I Do Now
            </h2>
            <p className="text-stone-600 mb-4 leading-relaxed">
              Today, I split my time between shepherding The Anchor Church and building tools through Bethany Labs, the company I founded to serve pastors and ministry leaders with better systems. SermonSift is the first product to come out of that work, alongside sister tools designed to help ministries repurpose and extend the reach of what they're already producing.
            </p>
            <p className="text-stone-600 leading-relaxed">
              I still preach every week. I still sit with people in hospital rooms and in coffee shops. And I still spend hours in the unglamorous, invisible work of sermon prep — because that's exactly where SermonSift was born.
            </p>
          </div>

          {/* Why I Made SermonSift */}
          <div className="card p-8 lg:p-12 max-w-4xl mx-auto">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1e3a8a] mb-6 text-center">
              Why I Made SermonSift
            </h2>
            <p className="text-lg text-stone-700 mb-4 leading-relaxed font-medium">
              I didn't build SermonSift because I stopped being a pastor and started being a developer. I built it because I'm still a pastor.
            </p>
            <p className="text-stone-600 mb-4 leading-relaxed">
              Every week, faithful preachers pour hours into study, exegesis, illustration-hunting, and outline-building — often alone, often late at night, often on top of counseling appointments, hospital visits, staff meetings, and family life. I've lived that rhythm for years. I know the particular exhaustion of staring at a blank page on a Tuesday with a Sunday deadline that never moves.
            </p>
            <p className="text-stone-600 mb-4 leading-relaxed">
              SermonSift exists to give pastors back their most valuable resource — time — without cutting corners on depth, biblical fidelity, or the Spirit-led work that no tool can replace. It's not meant to write your sermon for you. It's meant to sharpen your study, surface what you might have missed, and clear away the administrative weight so you can spend more of your week doing what only you can do: praying, studying, and shepherding your people.
            </p>
            <p className="text-stone-600 leading-relaxed">
              I see SermonSift as an extension of the same call I've carried since I first stepped behind a pulpit — kingdom stewardship applied to the tools available to this generation of church leaders. If it saves a pastor even a few hours a week, that's a few more hours for prayer, for people, and for the deeper work of ministry that first drew all of us into this calling.
            </p>
          </div>

        </div>
      </main>

      {/* CTA */}
      <section className="py-16 bg-[#1e3a8a]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to transform your sermon study?
          </h2>
          <p className="text-blue-200 mb-8">
            Join pastors and ministry leaders saving hours every week with SermonSift.
          </p>
          <Link to="/signup">
            <Button className="bg-white text-[#1e3a8a] hover:bg-stone-100 rounded-full px-8 py-3 font-medium shadow-lg">
              Start Your Free Trial
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#fafaf9] border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <span className="font-serif text-lg font-bold text-[#1e3a8a]">SermonSift</span>
              <p className="text-sm text-stone-400 mt-1">A Bethany Labs product</p>
            </div>
            <div className="flex items-center gap-6 text-sm text-stone-500">
              <Link to="/" className="hover:text-[#1e3a8a]">Home</Link>
              <span>•</span>
              <Link to="/pricing" className="hover:text-[#1e3a8a]">Pricing</Link>
              <span>•</span>
              <span>© {new Date().getFullYear()} Bethany Labs. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
